import { Button, Banner, useI18nContext } from "@procore/core-react";
import React, { useEffect, useState } from "react";
import { fetchCurrentAssignments } from "./queries";
import { Info } from "@procore/core-icons";
import { getCurrentAssignmentsFormatted } from "./helpers";
import type { AssignmentFormattedTypes, CurrentAssignmentsBannerProps } from "./types";
import { fetchUpcomingTimeOff } from "../../common/queries/queries";
import { ASSIGNMENT_CONTENT_LIMIT } from "./constants";

export const CurrentAssignmentsBanner = (props: CurrentAssignmentsBannerProps) => {
   const { personIds, personNames, showBanner } = props;
   const I18n = useI18nContext();

   const [assignmentsData, setAssignmentsData] = useState<
      Record<string, AssignmentFormattedTypes[]>
   >({});
   const [scheduleData, setScheduleData] = useState<Record<string, AssignmentFormattedTypes[]>>({});
   const [isExpanded, setIsExpanded] = useState<Record<string, boolean>>({});

   const [bannerVisibility, setBannerVisibility] = useState<Record<string, boolean>>({});

   useEffect(() => {
      setBannerVisibility(personIds.reduce((acc, id) => ({ ...acc, [id]: showBanner }), {}));
   }, [personIds, showBanner]);

   useEffect(() => {
      const fetchData = async () => {
         const newAssignments: Record<string, AssignmentFormattedTypes[]> = {};
         const newSchedules: Record<string, AssignmentFormattedTypes[]> = {};
         const newExpandedState: Record<string, boolean> = {};

         const fetchPromises = personIds.map(async (personId) => {
            const currentAssignments = await fetchCurrentAssignments(personId);
            const scheduledTimeOff = await fetchUpcomingTimeOff(personId);

            if (currentAssignments && scheduledTimeOff) {
               const formattedData = getCurrentAssignmentsFormatted(
                  currentAssignments?.data,
                  scheduledTimeOff,
               );
               newAssignments[personId] = [];
               newSchedules[personId] = formattedData ?? [];
               newExpandedState[personId] = false;
            }
         });

         await Promise.all(fetchPromises);

         setAssignmentsData(newAssignments);
         setScheduleData(newSchedules);
         setIsExpanded(newExpandedState);
      };

      fetchData();
   }, [personIds]);

   const toggleBannerVisibility = (personId: string) => {
      setBannerVisibility((prev) => ({
         ...prev,
         [personId]: false,
      }));
   };

   const handleButtonClick = (personId: string) => {
      setIsExpanded((prev) => ({
         ...prev,
         [personId]: !prev[personId],
      }));

      setAssignmentsData((prev) => ({
         ...prev,
         [personId]: prev[personId].length
            ? []
            : scheduleData[personId]?.slice(ASSIGNMENT_CONTENT_LIMIT) ?? [],
      }));
   };

   return (
      <>
         {personIds.map((personId, index) => {
            const personName = personNames[index];
            const assignments = assignmentsData[personId] ?? [];
            const schedules = scheduleData[personId] ?? [];

            return (
               schedules.length > 0 &&
               bannerVisibility[personId] && (
                  <Banner
                     {...props}
                     key={personId}
                     data-testid={`current-assignments-banner-${personId}`}
                     style={{ marginBottom: "10px" }}
                  >
                     <Banner.Icon icon={<Info size="md" />} />
                     <Banner.Content>
                        <Banner.Title>
                           {I18n.t("views.company.workforce_planning.assignment.schedule_for_text")}{" "}
                           {personName}
                        </Banner.Title>
                        <Banner.Body>
                           {schedules.slice(0, ASSIGNMENT_CONTENT_LIMIT).map((assignment) => (
                              <li key={assignment.assignmentId}>
                                 {assignment.formattedAssignmentDetails}
                              </li>
                           ))}
                           {assignments.map((assignment) => (
                              <li key={assignment.assignmentId}>
                                 {assignment.formattedAssignmentDetails}
                              </li>
                           ))}
                        </Banner.Body>
                     </Banner.Content>
                     {schedules.length > ASSIGNMENT_CONTENT_LIMIT && (
                        <Banner.Action>
                           <Button onClick={() => handleButtonClick(personId)}>
                              {isExpanded[personId]
                                 ? I18n.t("views.company.workforce_planning.show_less")
                                 : I18n.t("views.company.workforce_planning.show_more")}
                           </Button>
                        </Banner.Action>
                     )}
                     <Banner.Dismiss
                        onClick={() => toggleBannerVisibility(personId)}
                        data-testid={`current-assignments-banner-dismiss-${personId}`}
                     />
                  </Banner>
               )
            );
         })}
      </>
   );
};
